import { getSerializedNamesByCodes } from 'legacy/lut/components/widgets/public/onboarding/helpers/getSerializedNamesByCodes'
import { usePartnerData } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestDetail/actionView/usePartnerData'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectAppId } from 'core-components/sitebuilder/store/selectors/selectAppId'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import {
  FormCollectorCacheKey,
  onboardingFormCollectorApi,
} from 'onboarding/classes/services/api/OnboardingFormCollectorApi'
import { usePartnerTerritory } from 'onboarding/components/widgets/public/forms/hooks/usePartnerTerritory'
import { setIsDataUploadedForTransitionsMapping } from 'onboarding/features/onboardingSlice'
import { useParentRequest } from 'onboarding/hooks/useParentRequest'
import { useRequest } from 'onboarding/hooks/useRequest'
import { dropdownsService } from 'onboarding/services/dropdownsService'
import { requestorService } from 'onboarding/services/requestorService'

import { REQUESTOR_FILL_BUSINESS_UNITS } from '../configs'
import { calculateFormCompleteness } from '../helpers/calculateFormCompleteness'
import { TDropdownsCruds, TRequestorFill } from '../models'

const composeDropdownCrudCacheKey = (params: {
  dropdown: string
  requestorFillType: string
  isT1?: boolean
}) => `${params.dropdown}-${params.requestorFillType}${params.isT1 ? 'T1' : ''}`

interface IProps {
  setItem: (value: TRequestorFill) => void
  requestId: string
  type: string
  isT1?: boolean
}

export const useRequestorFillData = ({ setItem, requestId, type, isT1 }: IProps) => {
  const dispatch = useAppDispatch()
  const appId = useSelector(selectAppId)
  const [requestorWip, setRequestorWip] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const { req, reqWip } = useRequest(requestId)
  const { parentRequest, parentRequestWip } = useParentRequest(req.Parent)
  const { partner, partnerWip } = usePartnerData(parentRequest?.PartnerId ?? req.PartnerId)
  const [completenessFields, setCompletenessFields] = useState<string[]>([])
  const requestWip = reqWip || parentRequestWip
  const request = useMemo(() => parentRequest ?? req, [req, parentRequest])
  const partnerTerritory = usePartnerTerritory()

  useEffect(() => {
    const composeCompletenessFields = () => {
      let fields: string[] = []

      if (type === 'APJ') {
        fields = ['HPSigningEntity', 'TerritorySaleISO']
        if (isT1) {
          fields.push('ZYME')
        }
      }

      if (type === 'EMEA') {
        fields = ['LeadBU', 'HPSigningEntity', 'TerritorySaleISO']
      }

      setCompletenessFields(fields)
    }

    composeCompletenessFields()
  }, [type, isT1])

  useEffect(() => {
    const fetchFormData = async () => {
      if (request.EntryId) {
        const formData = await requestorService.getByRequestId(request.EntryId, true)
        if (formData) {
          setItem(formData)
        }
        setRequestorWip(false)
      }
    }

    fetchFormData()
  }, [request.EntryId])

  const cruds: TDropdownsCruds = useMemo(() => {
    return {
      hpSigningEntityApj: dropdownsService.getCrud({
        appId,
        dropdown: 'hpSigningEntityApj',
        cacheKey: composeDropdownCrudCacheKey({
          dropdown: 'hpSigningEntityApj',
          requestorFillType: type,
          isT1,
        }),
      }),
      hpSigningEntityEmea: dropdownsService.getCrud({
        appId,
        dropdown: 'hpSigningEntityEmea',
        cacheKey: composeDropdownCrudCacheKey({
          dropdown: 'hpSigningEntityEmea',
          requestorFillType: type,
          isT1,
        }),
      }),
      leadBU: dropdownsService.getCrud({
        appId,
        dropdown: 'leadBU',
        cacheKey: composeDropdownCrudCacheKey({
          dropdown: 'leadBU',
          requestorFillType: type,
          isT1,
        }),
      }),
      businessUnit: new Crud({
        fetcher: new Static(
          REQUESTOR_FILL_BUSINESS_UNITS.map((el) => ({
            key: el,
            label: el,
          }))
        ),
        keyField: 'key',
      }),
      salesStage: dropdownsService.getCrud({
        appId,
        dropdown: 'salesStage',
        cacheKey: composeDropdownCrudCacheKey({
          dropdown: 'salesStage',
          requestorFillType: type,
          isT1,
        }),
      }),
      bigDealEnablement: dropdownsService.getCrud({
        appId,
        dropdown: 'requestorFillBigDealEnablement',
        cacheKey: composeDropdownCrudCacheKey({
          dropdown: 'bigDealEnablement',
          requestorFillType: type,
          isT1,
        }),
      }),
    }
  }, [type, isT1, appId])

  const onSave = async (item: Partial<TRequestorFill>) => {
    let newItem = { ...item }
    setIsSaving(true)
    newItem.TerritorySale = newItem.TerritorySaleISO
      ? getSerializedNamesByCodes(newItem.TerritorySaleISO, partnerTerritory.countries)
      : null

    const formData = await requestorService.getByRequestId(request.EntryId, true)
    if (formData) {
      newItem = { ...formData, ...newItem }
    }
    const completeness = calculateFormCompleteness(newItem, completenessFields)
    newItem = isT1
      ? {
          ...newItem,
          Completeness: completeness,
          RequestId: request.EntryId,
          Category: newItem.Category ? newItem.Category : 'Administration',
        }
      : {
          ...newItem,
          Completeness: completeness,
          RequestId: request.EntryId,
        }

    let saved
    if (formData) {
      saved = await requestorService.update([newItem])
    } else {
      saved = await requestorService.create([newItem])
    }
    setIsSaving(false)
    onboardingFormCollectorApi.triggerCrudOnChange(FormCollectorCacheKey.DataCollectorTable)
    dispatch(setIsDataUploadedForTransitionsMapping(true))

    return saved[0] ?? null
  }

  return {
    cruds,
    isLoading: requestorWip || partnerWip || requestWip,
    isSaving,
    onSave,
    partner,
    partnerTerritory,
    requestCountryISO: request.CountryISO,
  }
}
